import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Send, Phone, Mail } from 'lucide-react';
import { api } from '@/api';
import { TID } from '@/testIds';

const PROJECT_TYPES = ['Car','Truck','Trailer','Van','Fleet','RV','Wall','Floor','Window','Custom'];

export default function QuoteSection() {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', company: '',
    project_type: 'Car', year: '', make: '', model: '', details: '', subject: '',
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // If the Wrapulator "Save Quote" button was clicked, prefill the details field
  // with the calc summary so the user only needs to add contact info.
  // Uses both: (1) sessionStorage snapshot on mount (page reload scenarios), and
  // (2) a live custom event dispatched by Wrapulator.saveQuote() so it works
  //    without a page reload since both components are already mounted.
  useEffect(() => {
    const apply = (summary) => {
      if (!summary) return;
      setForm((f) => ({ ...f, details: summary, subject: 'Wrapulator quote request' }));
    };
    try {
      const prefill = sessionStorage.getItem('mi_prefill_quote');
      if (prefill) {
        apply(prefill);
        sessionStorage.removeItem('mi_prefill_quote');
      }
    } catch {}
    const handler = (e) => {
      apply(e?.detail || '');
      try { sessionStorage.removeItem('mi_prefill_quote'); } catch {}
    };
    window.addEventListener('mi:prefill-quote', handler);
    return () => window.removeEventListener('mi:prefill-quote', handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      const { data } = await api.post('/quote', payload);
      toast.success(data.message || 'Quote received. We will reach out shortly.');
      setForm({
        first_name: '', last_name: '', email: '', phone: '', company: '',
        project_type: 'Car', year: '', make: '', model: '', details: '', subject: '',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Please check the form and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-32 border-b mi-line">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-12">
        <div className="min-w-0">
          <div className="mi-eyebrow mb-3">// GET A QUOTE</div>
          <h2 className="mi-headline text-5xl md:text-6xl mb-6">
            Tell us <span className="text-[#E63946]">what to wrap.</span>
          </h2>
          <p className="text-white/70 max-w-md leading-relaxed mb-8">
            Every request lands directly with sales@motorinks.com. We aim to respond within one business day —
            usually much sooner.
          </p>
          <div className="space-y-4">
            <a href="tel:8327912692" className="flex items-center gap-4 group" data-testid="quote-side-phone">
              <div className="w-11 h-11 border mi-line-strong grid place-items-center group-hover:border-[#E63946] transition-colors">
                <Phone className="w-5 h-5 text-[#E63946]" />
              </div>
              <div>
                <div className="mi-eyebrow">CALL / TEXT</div>
                <div className="mi-headline text-2xl">832.791.2692</div>
              </div>
            </a>
            <a href="mailto:sales@motorinks.com" className="flex items-center gap-4 group" data-testid="quote-side-email">
              <div className="w-11 h-11 border mi-line-strong grid place-items-center group-hover:border-[#E63946] transition-colors">
                <Mail className="w-5 h-5 text-[#E63946]" />
              </div>
              <div>
                <div className="mi-eyebrow">EMAIL</div>
                <div className="mi-headline text-xl sm:text-2xl break-all">sales@motorinks.com</div>
              </div>
            </a>
          </div>
          <div className="mt-10 border mi-line-strong p-5">
            <div className="mi-eyebrow mb-2">// OUR TURNAROUND</div>
            <div className="mi-headline text-3xl">48 HR AVG</div>
            <p className="text-white/60 text-sm mt-2">Fleet & multi-unit projects scheduled by seat availability. Rush service available.</p>
          </div>
        </div>

        <form onSubmit={submit} className="mi-surface p-6 lg:p-8">
          <div className="grid grid-cols-2 gap-4">
            <Input required placeholder="First name" value={form.first_name} onChange={upd('first_name')} testid={TID.quoteFirstName} />
            <Input required placeholder="Last name" value={form.last_name} onChange={upd('last_name')} testid={TID.quoteLastName} />
            <Input required type="email" placeholder="Email" value={form.email} onChange={upd('email')} testid={TID.quoteEmail} className="col-span-2" />
            <Input placeholder="Phone" value={form.phone} onChange={upd('phone')} testid={TID.quotePhone} />
            <Input placeholder="Company (optional)" value={form.company} onChange={upd('company')} testid={TID.quoteCompany} />

            <label className="col-span-2 block">
              <div className="mi-eyebrow mb-2 text-white/70">What's to be wrapped?</div>
              <select
                data-testid={TID.quoteProject}
                value={form.project_type}
                onChange={upd('project_type')}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm"
              >
                {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>

            {['Car','Truck','Van','Fleet','RV','Trailer'].includes(form.project_type) && (
              <>
                <Input placeholder="Year" value={form.year} onChange={upd('year')} testid={TID.quoteYear} />
                <Input placeholder="Make" value={form.make} onChange={upd('make')} testid={TID.quoteMake} />
                <Input placeholder="Model" value={form.model} onChange={upd('model')} testid={TID.quoteModel} className="col-span-2" />
              </>
            )}

            <textarea
              required
              data-testid={TID.quoteDetails}
              placeholder="Give us the details — colors, coverage, timeline, quantities…"
              value={form.details}
              onChange={upd('details')}
              rows={5}
              className="col-span-2 w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25"
            />
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="mi-eyebrow text-white/50">// SENT TO SALES@MOTORINKS.COM</div>
            <button
              type="submit"
              disabled={busy}
              data-testid={TID.quoteSubmit}
              className="mi-btn-red disabled:opacity-60"
            >
              {busy ? 'Sending…' : (<>Send request <Send className="w-4 h-4" /></>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Input({ testid, className = '', ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className={`w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25 ${className}`}
    />
  );
}
